/**
 * The one nav-layout resolver (#14279), and the landing-page resolver beside
 * it.
 *
 * `resolveNavLayout` is what the header renders from: the org's saved lists,
 * or `defaultNavLayout` when nothing is saved, minus every entry whose gate
 * fails for this viewer. Pure — the header resolves the gates (flags,
 * permissions, onboarding sections) and hands the answers in.
 *
 * `resolveLandingPath` answers where `/` lands: the saved landing page, else
 * `/home` where the org's layout shows Home (#16727), else `/today`. Since
 * #15209 no feature flag replaces, renames, reorders or redirects anything
 * here — flags only hide an unavailable entry.
 *
 * CLIENT-SAFE, like `./vocabulary`.
 */

import { AUDIENCE_ORG, type FeatureAudience } from '@/lib/feature-flag-audience'

import {
  ENTRIES,
  landingPathSchema,
  readEntryKey,
  type EntryKey,
  type LandingPath,
} from './vocabulary'
import {
  userMenuLayoutSchema,
  type UserMenuLayout,
} from './user-menu-vocabulary'

export type { UserMenuLayout }

export interface NavLayout {
  left: EntryKey[]
  right: EntryKey[]
}

/**
 * Whether each entry's own gate passes for the viewer — ONE map for every
 * entry, wherever it is placed (#15689): the sidebar, the header and the
 * user menu all read it (`buildEntryAvailability`).
 */
export type ViewerEntryAvailability = Record<EntryKey, boolean>

/**
 * Who one entry reaches in an org, for the admin editor (#15403): on for the
 * org (optionally hidden for some people), on for some people only, or off.
 * Not the header's per-viewer `NavAvailability`, which stays a boolean — the
 * header asks about one viewer, the editor about everyone. A non-flag gate
 * (a plan, AI chat) is `org` or `off`; an entry whose gate depends on the
 * viewer (onboarding, role) is `org`, its condition in its `gate` text.
 */
export type EntryAvailability = FeatureAudience

/**
 * `getOrgNavAvailability`'s answer: every entry's audience, read by both
 * editors (#15689) — an entry is gated identically wherever it is placed.
 */
export type NavEntryAvailability = Record<EntryKey, EntryAvailability>

/**
 * The left side of the header's default layout. No Home (#16727): an org
 * shows Home by listing it in a saved layout — the retirement migration of
 * the Home flag wrote it into the layouts of the orgs that had it.
 *
 * FROZEN COPIES. That migration
 * (`prisma/migrations/20261030154018_retire_chrome_feature_flags`) wrote
 * the then-default sidebar layout, plus Home, as a literal into the saved
 * layout of every org that had Home and nothing saved. Those orgs no longer
 * read this default, so a later change here does not reach them — change
 * their rows too, or accept the drift.
 */
const DEFAULT_LEFT: readonly EntryKey[] = [
  'today',
  'plan',
  'actionboard',
  'notes',
  'pulse',
]

/**
 * The right side of the header's default layout: the header's entries before
 * #15689, named rather than derived from the vocabulary, so an entry added to
 * the vocabulary (Templates, or a former user-menu-only entry) joins no
 * default and an org with nothing saved sees no change.
 */
const DEFAULT_RIGHT: readonly EntryKey[] = [
  'orgs',
  'priorities',
  'people',
  'meetings',
  'performance',
  'plans',
  'boards',
  'search',
  'notifications',
  'feedback',
]

/**
 * The layout an org with nothing saved gets: one static list (#15209).
 *
 * No flag argument, deliberately. The navigation config is the only thing
 * that decides which header entries render; a flag may HIDE an entry whose
 * surface is unavailable — that is `resolveNavLayout`'s availability filter —
 * but may not replace, rename or reorder one. The old composition rules
 * (Priorities replacing the icon cluster, ActionBoard taking Pulse's slot,
 * Notes only with the note taker) were exactly that, and are gone; an org
 * that wants one of those shapes saves a layout that lists it.
 */
export function defaultNavLayout(): NavLayout {
  return {
    left: [...DEFAULT_LEFT],
    right: [...DEFAULT_RIGHT],
  }
}

/**
 * The side navigation's layout for an org with nothing saved (#15668): the
 * stored sides are its zones — `left` the main list, `right` the bottom,
 * above the user menu. Search leads the main list, then every destination in
 * the header default's order; Notifications and Feedback sit at the bottom.
 * Since #16727 the side navigation is every viewer's, and this is the
 * default every org with nothing saved renders.
 */
const SIDEBAR_BOTTOM: readonly EntryKey[] = ['notifications', 'feedback']

export function defaultSidebarNavLayout(): NavLayout {
  const header = defaultNavLayout()
  const destinations = [...header.left, ...header.right].filter(
    key => key !== 'search' && !SIDEBAR_BOTTOM.includes(key),
  )
  return { left: ['search', ...destinations], right: [...SIDEBAR_BOTTOM] }
}

/**
 * The default sidebar layout with Home right after Today (#16727) — the
 * layout the flag-retirement migration wrote for an org that had Home and
 * nothing saved, and the one a new demo org is created with, so a demo
 * org's Home does not depend on whether it existed when that migration ran.
 * (Like `defaultSidebarNavLayout`, this is today's default: the migration's
 * rows are a frozen copy — see `DEFAULT_LEFT`.)
 */
export function defaultSidebarNavLayoutWithHome(): NavLayout {
  const { left, right } = defaultSidebarNavLayout()
  const today = left.indexOf('today')
  left.splice(today === -1 ? 0 : today + 1, 0, 'home')
  return { left, right }
}

/**
 * What the header renders: listed AND available. An unlisted entry never
 * renders, whatever its gate says; a listed one renders only when its gate
 * passes.
 */
export function resolveNavLayout(
  saved: NavLayout | null,
  availability: ViewerEntryAvailability,
  fallback: () => NavLayout = defaultNavLayout,
): NavLayout {
  const layout = saved ?? fallback()
  const shown = (key: EntryKey) => availability[key]
  return {
    left: layout.left.filter(shown),
    right: layout.right.filter(shown),
  }
}

/**
 * Reads the two `Json?` columns. `null` in EITHER means "use the default" —
 * the write path sets and clears them together.
 *
 * Defensive rather than strict: this runs in the app shell on every page, so
 * a key a later release retired, or a repeat, is dropped instead of failing
 * the header. Writes are strict (`navLayoutSchema`); reads forgive. A legacy
 * key (`pulse-check`) reads as its entry (`readEntryKey`, #15689).
 */
export function parseStoredNavLayout(
  left: unknown,
  right: unknown,
): NavLayout | null {
  if (!Array.isArray(left) || !Array.isArray(right)) return null
  const seen = new Set<EntryKey>()
  const read = (values: unknown[]): EntryKey[] => {
    const keys: EntryKey[] = []
    for (const value of values) {
      const key = readEntryKey(value)
      if (key === null || seen.has(key)) continue
      seen.add(key)
      keys.push(key)
    }
    return keys
  }
  return { left: read(left), right: read(right) }
}

/** Reads `Organization.landingPath`; anything off the closed list is unset. */
export function parseStoredLandingPath(value: unknown): LandingPath | null {
  const parsed = landingPathSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/** Whether a nav layout lists Home, on either side (#16727). */
export function layoutShowsHome(layout: NavLayout | null): boolean {
  return (
    layout !== null &&
    (layout.left.includes('home') || layout.right.includes('home'))
  )
}

/**
 * Whether the org's navigation shows Home (#16727): its saved layout or its
 * saved user menu lists it. Neither default does, so `null` settings — or
 * nothing saved — is `false`. This is where Home is OFFERED (the command
 * bar's Home row, the 404 page's button); `/home` itself opens for everyone
 * by URL.
 */
export function orgNavShowsHome(
  settings: {
    layout: NavLayout | null
    userMenu: UserMenuLayout | null
  } | null,
): boolean {
  if (settings === null) return false
  return (
    layoutShowsHome(settings.layout) ||
    (settings.userMenu?.sections.some(section =>
      section.items.includes('home'),
    ) ??
      false)
  )
}

/**
 * Where `/` lands: the saved landing page; with none saved, `/home` where the
 * org's saved layout shows Home (#16727), and `/today` otherwise (#15209).
 * No flag moves `/` — the #14243 note-taker fallback to `/notes` is gone.
 * Whether the viewer can OPEN the target is the caller's question
 * (`canOpenLandingPath`); this answers only what the config says. No default
 * layout lists Home, so `layout: null` (nothing saved) lands on Today.
 */
export function resolveLandingPath(input: {
  landingPath: LandingPath | null
  layout?: NavLayout | null
}): LandingPath {
  if (input.landingPath) return input.landingPath
  return layoutShowsHome(input.layout ?? null) ? '/home' : '/today'
}

/**
 * The nav entry whose org-level gate decides whether a landing target opens,
 * or `null` for a target with no gate that 404s. `/actionboard` sits behind
 * its own flag and `notFound()`s when that gate is off. `/plan` renders an
 * empty state without a plan, and `/today`, `/pulse`, `/notes`, `/events`
 * (#15677) and `/priorities` (#16722) have no gate at all.
 * `/home` sits behind the Home entry's gate: AI chat (#16727).
 *
 * Exhaustive on purpose: a new landing path has to say what gates it. The
 * server's per-viewer twin is `canOpenLandingPath` (`./landing-access`).
 */
export const LANDING_PATH_GATE: Record<LandingPath, EntryKey | null> = {
  '/today': null,
  '/notes': null,
  '/events': null,
  '/priorities': null,
  '/plan': null,
  '/actionboard': 'actionboard',
  '/pulse': null,
  '/home': 'home',
}

/**
 * Who a landing target opens for (#15403): its gating entry's audience, or
 * `org` for a target with no gate. A visitor it does not open for lands on
 * the default instead (`canOpenLandingPath`).
 */
export function landingPathAvailability(
  path: LandingPath,
  availability: NavEntryAvailability,
): EntryAvailability {
  const gate = LANDING_PATH_GATE[path]
  return gate === null ? AUDIENCE_ORG : availability[gate]
}

/** The editor's "why not" for a closed landing target: the entry's gate. */
export function landingPathGateText(path: LandingPath): string | null {
  const gate = LANDING_PATH_GATE[path]
  return gate === null ? null : ENTRIES[gate].gate
}

/**
 * The user menu an org with nothing saved gets (#14657): the pre-#14657
 * menu's configurable middle, section for section. Its first and last groups
 * had no heading — only a separator — so their titles are `null`; "Work" is
 * the one heading that menu drew. Named entry by entry (#15689): the
 * vocabulary is shared with the sidebar now, so deriving the default from it
 * would put every new entry in the menu.
 */
const DEFAULT_USER_MENU: readonly {
  title: string | null
  items: readonly EntryKey[]
}[] = [
  {
    title: null,
    items: ['ai-chat', 'command-bar', 'pulse', 'notifications', 'feedback'],
  },
  {
    title: 'Work',
    items: [
      'orgs',
      'people',
      'meetings',
      'performance',
      'plans',
      'boards',
      'company-info',
    ],
  },
  {
    title: null,
    items: [
      'presentation-mode',
      'copy-page-link',
      'keyboard-shortcuts',
      'training',
      'whats-new',
      'surveys',
      'ai-integrations',
      'demo-mode',
    ],
  },
]

export function defaultUserMenuLayout(): UserMenuLayout {
  return {
    sections: DEFAULT_USER_MENU.map(section => ({
      title: section.title,
      items: [...section.items],
    })),
  }
}

/**
 * What the user menu renders between its pinned top and bottom: the saved
 * sections (or the default), each entry kept only when its gate passes, and
 * a section with no entry left dropped whole — no heading, no separator
 * (#2507: a heading over nothing is noise). Listing never grants access.
 */
export function resolveUserMenuLayout(
  saved: UserMenuLayout | null,
  availability: ViewerEntryAvailability,
): UserMenuLayout {
  const layout = saved ?? defaultUserMenuLayout()
  return {
    sections: layout.sections
      .map(section => ({
        title: section.title,
        items: section.items.filter(key => availability[key]),
      }))
      .filter(section => section.items.length > 0),
  }
}

/**
 * Maps legacy keys to their entry (`pulse-check` → `pulse`, #15689) and drops
 * keys the vocabulary no longer knows, so retiring an entry costs an org that
 * entry and not its whole saved menu. A legacy key whose entry is already
 * listed is dropped rather than read as a repeat; a genuine repeat is left
 * for the schema to refuse. A pinned entry's name is not in the vocabulary
 * either, and dropping it is harmless: pinned entries render from their own
 * hard-coded blocks whatever is saved.
 */
function withCurrentUserMenuKeys(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value
  const { sections } = value as { sections?: unknown }
  if (!Array.isArray(sections)) return value
  const itemsOf = (section: unknown): unknown[] | null => {
    if (typeof section !== 'object' || section === null) return null
    const { items } = section as { items?: unknown }
    return Array.isArray(items) ? items : null
  }
  const listed = new Set<unknown>(sections.flatMap(s => itemsOf(s) ?? []))
  return {
    ...value,
    sections: sections.map(section => {
      const items = itemsOf(section)
      if (items === null) return section
      const keys: EntryKey[] = []
      for (const item of items) {
        const key = readEntryKey(item)
        if (key === null) continue
        if (key !== item) {
          // A legacy spelling: once, and only where the entry is not listed.
          if (listed.has(key)) continue
          listed.add(key)
        }
        keys.push(key)
      }
      return { ...(section as object), items: keys }
    }),
  }
}

/**
 * Reads `Organization.userMenuSections`. Legacy keys are mapped, and item
 * keys the vocabulary no longer knows or repeats are dropped, first
 * (`withCurrentUserMenuKeys`); anything else that does not pass the
 * write-side schema — NULL, a bad title, too many sections — reads
 * as `null`, "use the default": this runs in the app shell, and the default
 * menu is always a working menu.
 */
export function parseStoredUserMenuLayout(
  value: unknown,
): UserMenuLayout | null {
  const parsed = userMenuLayoutSchema.safeParse(withCurrentUserMenuKeys(value))
  return parsed.success ? parsed.data : null
}
